import { apiFetch, API_BASE, fetchAuthorizedBlob, uploadWithToken } from './core.js';

export const auth = {
  login: (email, password, turnstileToken) => apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password, turnstileToken }) }),
  me: () => apiFetch('/auth/me'),
  logout: () => apiFetch('/auth/logout', { method: 'POST' }),
};

export const dashboard = {
  summary: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/summary?${params.toString()}`);
  },
  salesChart: (period = 'week', from, to) => {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/sales-chart?${params.toString()}`);
  },
  topProducts: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/top-products?${params.toString()}`);
  },
  recentOrders: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/recent-orders?${params.toString()}`);
  },
  orderStatusDistribution: () => apiFetch('/dashboard/order-status-distribution'),
  paymentStatusDistribution: () => apiFetch('/dashboard/payment-status-distribution'),
  revenueDistribution: (period = 'week', from, to) => {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/revenue-distribution?${params.toString()}`);
  },
  orderTrends: (period = 'day', from, to, status) => {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    if (status) params.set('status', status);
    return apiFetch(`/dashboard/order-trends?${params.toString()}`);
  },
  revenueByInfluencer: (from, to) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return apiFetch(`/dashboard/revenue-by-influencer?${params.toString()}`);
  },
  trafficSummary: (from, to) => apiFetch(`/dashboard/traffic-summary?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  topPages: (from, to) => apiFetch(`/dashboard/top-pages?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  topProductViews: (from, to) => apiFetch(`/dashboard/top-product-views?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  audienceBreakdown: (from, to) => apiFetch(`/dashboard/audience-breakdown?${new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) })}`),
  exportExcel: (period = 'week', from, to) => {
    const params = new URLSearchParams({ period });
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return fetchAuthorizedBlob(`/dashboard/export.xlsx?${params.toString()}`, { fileNameFallback: `dashboard-miskipop-${period}.xlsx` });
  },
  exportOrdersExcel: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.paymentStatus) params.set('paymentStatus', filters.paymentStatus);
    if (filters.trackingStatus) params.set('trackingStatus', filters.trackingStatus);
    if (filters.productId) params.set('productId', filters.productId);
    if (filters.country) params.set('country', filters.country);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.search) params.set('search', filters.search);
    if (filters.columns) params.set('columns', filters.columns);
    return fetchAuthorizedBlob(`/dashboard/orders-export.xlsx?${params.toString()}`, { fileNameFallback: 'ordenes-miskipop.xlsx' });
  },
  salesByCountry: (productId) => {
    const params = new URLSearchParams();
    if (productId) params.set('productId', productId);
    return apiFetch(`/dashboard/sales-by-country?${params.toString()}`);
  },
};


export const products = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.country) q.set('country', params.country);
    if (params.productId) q.set('productId', params.productId);
    if (params.categoryId) q.set('categoryId', params.categoryId);
    if (params.status) q.set('status', params.status);
    if (params.featured !== undefined) q.set('featured', params.featured);
    if (params.lastChance !== undefined) q.set('lastChance', params.lastChance);
    if (params.upcoming !== undefined) q.set('upcoming', params.upcoming);
    if (params.influencerId) q.set('influencerId', params.influencerId);
    if (params.locked !== undefined) q.set('locked', params.locked);
    if (params.inDrop !== undefined) q.set('inDrop', params.inDrop);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.page) q.set('page', params.page);
    if (params.limit) q.set('limit', params.limit);
    if (params.inStock) q.set('inStock', params.inStock);
    if (params.onSale) q.set('onSale', params.onSale);
    return apiFetch(`/products?${q.toString()}`);
  },
  lock: (id, lockUntil) => apiFetch(`/products/${id}/lock`, { method: 'PATCH', body: JSON.stringify({ lockUntil }) }),
  drop: (id, drop) => apiFetch(`/products/${id}/drop`, { method: 'PATCH', body: JSON.stringify(drop) }),
  progress: (idOrSlug) => apiFetch(`/products/${idOrSlug}/progress`),
  get: (id) => apiFetch(`/products/${id}`),
  create: (data) => apiFetch('/products', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/products/${id}`, { method: 'DELETE' }),
  reorder: (items) => apiFetch('/products/batch/reorder', { method: 'PATCH', body: JSON.stringify({ items }) }),
};

export const categories = {
  list: () => apiFetch('/categories'),
  create: (data) => apiFetch('/categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/categories/${id}`, { method: 'DELETE' }),
};

export const orders = {
  flowAudit: (email) => apiFetch(`/orders/flow-audit?email=${encodeURIComponent(email)}`),
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.paymentStatus) q.set('paymentStatus', params.paymentStatus);
    if (params.country) q.set('country', params.country);
    if (params.productId) q.set('productId', params.productId);
    if (params.trackingStatus) q.set('trackingStatus', params.trackingStatus);
    if (params.q) q.set('q', params.q);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.page) q.set('page', params.page);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/orders?${q.toString()}`);
  },
  get: (id) => apiFetch(`/orders/${id}`),
  updateStatus: (id, payload) => apiFetch(`/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify(payload) }),
  replaceTracking: (id, payload) => apiFetch(`/orders/${id}/tracking-replacement`, { method: 'POST', body: JSON.stringify(payload) }),
  clearManualTracking: (id, payload) => apiFetch(`/orders/${id}/tracking-manual/clear`, { method: 'POST', body: JSON.stringify(payload) }),
  sendShipmentTestEmail: (id, trackingNumber) => apiFetch(`/orders/${id}/shipment-email-test`, { method: 'POST', body: JSON.stringify({ trackingNumber }) }),
  markRefunded: (id, payload = {}) => apiFetch(`/orders/${id}/mark-refunded`, { method: 'POST', body: JSON.stringify(payload) }),
  updateAddresses: (id, payload) => apiFetch(`/orders/${id}/addresses`, { method: 'PATCH', body: JSON.stringify(payload) }),
  addressReview: (params = {}) => {
    const q = new URLSearchParams();
    if (typeof params === 'number') q.set('limit', params);
    else {
      if (params.limit) q.set('limit', params.limit);
      if (params.page) q.set('page', params.page);
      if (params.productId) q.set('productId', params.productId);
      if (params.issue) q.set('issue', params.issue);
      if (params.q) q.set('q', params.q);
    }
    return apiFetch(`/orders/address-review?${q.toString()}`);
  },
  batchOmitAddressReview: (ids, reason) => apiFetch('/orders/address-review/batch-omit', { method: 'POST', body: JSON.stringify({ ids, reason }) }),
  resolveAddressReview: (id, payload) => apiFetch(`/orders/${id}/address-review`, { method: 'PATCH', body: JSON.stringify(payload) }),
  omitAddressReview: (id, reason) => apiFetch(`/orders/${id}/address-review/omit`, { method: 'POST', body: JSON.stringify({ reason }) }),
  remove: (id) => apiFetch(`/orders/${id}`, { method: 'DELETE' }),
  syncYunexpress: (id) => apiFetch(`/orders/${id}/yunexpress/sync`, { method: 'POST' }),
  batchStatus: (ids, status) => apiFetch('/orders/batch-status', { method: 'POST', body: JSON.stringify({ ids, status }) }),
  batchSyncPayment: (ids) => apiFetch('/orders/batch-sync-payment', { method: 'POST', body: JSON.stringify({ ids }) }),
  batchTracking: (rows) => apiFetch('/orders/batch-tracking', { method: 'POST', body: JSON.stringify({ rows }) }),
  productsSold: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.categoryId) q.set('categoryId', params.categoryId);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/orders/products-sold?${q.toString()}`);
  }
};

export const landings = {
  list: () => apiFetch('/landings'),
  get: (id) => apiFetch(`/landings/${id}`),
  getBySubdomain: (subdomain, cacheBust) => apiFetch(`/landings/by-subdomain?subdomain=${encodeURIComponent(subdomain)}${cacheBust ? `&_t=${cacheBust}` : ''}`),
  create: (data) => apiFetch('/landings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/landings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/landings/${id}`, { method: 'DELETE' }),
  createSection: (id, data) => apiFetch(`/landings/${id}/sections`, { method: 'POST', body: JSON.stringify(data) }),
  updateSection: (id, sectionId, data) => apiFetch(`/landings/${id}/sections/${sectionId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeSection: (id, sectionId) => apiFetch(`/landings/${id}/sections/${sectionId}`, { method: 'DELETE' }),
  assignProduct: (id, data) => apiFetch(`/landings/${id}/products`, { method: 'POST', body: JSON.stringify(data) }),
  removeProduct: (id, productId) => apiFetch(`/landings/${id}/products/${productId}`, { method: 'DELETE' }),
  getComments: (id) => apiFetch(`/landings/${id}/comments`),
  updateComment: (id, commentId, data) => apiFetch(`/landings/${id}/comments/${commentId}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeComment: (id, commentId) => apiFetch(`/landings/${id}/comments/${commentId}`, { method: 'DELETE' }),
};

export const templates = {
  list: () => apiFetch('/templates'),
  create: (data) => apiFetch('/templates', { method: 'POST', body: JSON.stringify(data) }),
};

export const influencers = {
  list: () => apiFetch('/influencers'),
  create: (data) => apiFetch('/influencers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/influencers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/influencers/${id}`, { method: 'DELETE' }),
  provisionHostname: (id, hostname) => apiFetch(`/influencers/${id}/hostnames/provision`, { method: 'POST', body: JSON.stringify({ hostname }) }),
  removeHostname: (id, hostname) => apiFetch(`/influencers/${id}/hostnames/${encodeURIComponent(hostname)}`, { method: 'DELETE' }),
  listHostnames: (id) => apiFetch(`/influencers/${id}/hostnames`),
  wildcardStatus: () => apiFetch('/influencers/dns/wildcard-status'),
  provisionWildcard: () => apiFetch('/influencers/dns/wildcard-provision', { method: 'POST' }),
  verifyDns: (hostname) => apiFetch(`/influencers/dns/verify?hostname=${encodeURIComponent(hostname)}`),
};

export const influencerFinances = {
  list: () => apiFetch('/influencers/finance'),
  get: (id) => apiFetch(`/influencers/finance/${id}`),
  createAdjustment: (id, data) => apiFetch(`/influencers/finance/${id}/adjustments`, { method: 'POST', body: JSON.stringify(data) }),
  voidAdjustment: (id, adjustmentId, reason) => apiFetch(`/influencers/finance/${id}/adjustments/${adjustmentId}/void`, { method: 'POST', body: JSON.stringify({ reason }) }),
  setAdjustmentVisibility: (id, adjustmentId, visibleToInfluencer) => apiFetch(`/influencers/finance/${id}/adjustments/${adjustmentId}/visibility`, { method: 'PATCH', body: JSON.stringify({ visibleToInfluencer }) }),
  setBalance: (id, data) => apiFetch(`/influencers/finance/${id}/set-balance`, { method: 'POST', body: JSON.stringify(data) }),
  createPayout: (id, data) => apiFetch(`/influencers/finance/${id}/payouts`, { method: 'POST', body: JSON.stringify(data) }),
  updatePayout: (id, data) => apiFetch(`/influencers/finance/payouts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

export const staff = {
  list: () => apiFetch('/staff'),
  get: (id) => apiFetch(`/staff/${id}`),
  create: (data) => apiFetch('/staff', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/staff/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  toggleStatus: (id, isActive) => apiFetch(`/staff/${id}/status`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
  remove: (id) => apiFetch(`/staff/${id}`, { method: 'DELETE' }),
};

export const documents = {
  list: (recipientUserId) => apiFetch(`/documents${recipientUserId ? `?recipientUserId=${encodeURIComponent(recipientUserId)}` : ''}`),
  create: (file, fields) => uploadWithToken('/documents', file, 'contracts', 'admin_token', fields),
  send: (id) => apiFetch(`/documents/${id}/send`, { method: 'POST' }),
  update: (id, data) => apiFetch(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/documents/${id}`, { method: 'DELETE' }),
  view: (id) => apiFetch(`/documents/${id}/view`, { method: 'POST' }),
  download: (id, kind = 'original') => `${API_BASE}/documents/${id}/download/${kind}`,
};

export const customersAdmin = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.page) q.set('page', params.page);
    if (params.read !== undefined) q.set('read', String(params.read));
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/customers?${q.toString()}`);
  },
  filterOptions: () => apiFetch('/customers/filter-options'),
  analytics: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.country) q.set('country', params.country);
    if (params.productId) q.set('productId', params.productId);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    return apiFetch(`/customers/analytics?${q.toString()}`);
  },
  exportExcel: (params = {}) => {
    const q = new URLSearchParams();
    if (params.q) q.set('q', params.q);
    if (params.country) q.set('country', params.country);
    if (params.productId) q.set('productId', params.productId);
    if (params.columns) q.set('columns', params.columns);
    return fetchAuthorizedBlob(`/customers/export.xlsx?${q.toString()}`, { fileNameFallback: 'usuarios-miskipop.xlsx' });
  },
  detail: (id) => apiFetch(`/customers/${id}/detail`),
  orderCandidates: (id, q = '') => apiFetch(`/customers/${id}/order-candidates?q=${encodeURIComponent(q)}`),
  reassignOrders: (id, payload) => apiFetch(`/customers/${id}/reassign-orders`, { method: 'POST', body: JSON.stringify(payload) }),
  merge: (id, payload) => apiFetch(`/customers/${id}/merge`, { method: 'POST', body: JSON.stringify(payload) }),
  updatePassword: (id, password) => apiFetch(`/customers/${id}/password`, { method: 'PATCH', body: JSON.stringify({ password }) }),
};

export const support = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.search) q.set('search', params.search);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/support?${q.toString()}`);
  },
  respond: (id, message) => apiFetch(`/support/${id}/responses`, { method: 'POST', body: JSON.stringify({ message }) }),
  updateStatus: (id, status) => apiFetch(`/support/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
};

export const uploads = {
  presign: (fileName, contentType, folder = 'products') => apiFetch('/uploads/presign', { method: 'POST', body: JSON.stringify({ fileName, contentType, folder }) }),
  upload: (file, folder = 'products') => uploadWithToken('/uploads/upload', file, folder),
  remove: (key) => apiFetch('/uploads', { method: 'DELETE', body: JSON.stringify({ key }) }),
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.folder) q.set('folder', params.folder);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/uploads/list?${q.toString()}`);
  },
};

export const shipping = {
  list: () => apiFetch('/shipping'),
  create: (data) => apiFetch('/shipping', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/shipping/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  deactivate: (id) => apiFetch(`/shipping/${id}/deactivate`, { method: 'POST' }),
  remove: (id) => apiFetch(`/shipping/${id}`, { method: 'DELETE' }),
};

export const settings = {
  get: (key) => apiFetch(`/settings/${key}`),
  update: (key, value) => apiFetch(`/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  getShipmentEmailTemplate: () => apiFetch('/settings/email-templates/shipment-in-transit'),
  updateShipmentEmailTemplate: (template) => apiFetch('/settings/email-templates/shipment-in-transit', { method: 'PUT', body: JSON.stringify(template) }),
  previewShipmentEmailTemplate: (template) => apiFetch('/settings/email-templates/shipment-in-transit/preview', { method: 'POST', body: JSON.stringify({ template }) }),
  getYunexpressConfig: () => apiFetch('/settings/yunexpress/config'),
  updateYunexpressConfig: (config) => apiFetch('/settings/yunexpress/config', { method: 'PUT', body: JSON.stringify(config) }),
  testYunexpress: (config) => apiFetch('/settings/yunexpress/test', { method: 'POST', body: JSON.stringify(config) }),
  syncYunexpressProducts: () => apiFetch('/settings/yunexpress/sync-products', { method: 'POST' }),
};

export const mailbox = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.category) q.set('category', params.category);
    if (params.search) q.set('search', params.search);
    if (params.folder) q.set('folder', params.folder);
    if (params.page) q.set('page', params.page);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/mailbox/messages?${q.toString()}`);
  },
  stats: () => apiFetch('/mailbox/stats'),
  readStats: () => apiFetch('/mailbox/read-stats'),
  audience: (productId) => apiFetch(`/mailbox/audience${productId ? `?productId=${encodeURIComponent(productId)}` : ''}`),
  bulkSend: (payload) => apiFetch('/mailbox/bulk-send', { method: 'POST', body: JSON.stringify(payload) }),
  campaign: (id) => apiFetch(`/mailbox/bulk-campaigns/${id}`),
  sync: (payload = {}) => apiFetch('/mailbox/sync', { method: 'POST', body: JSON.stringify(payload) }),
  exportExcel: (params = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.category) query.set('category', params.category);
    if (params.search) query.set('search', params.search);
    if (params.folder) query.set('folder', params.folder);
    return fetchAuthorizedBlob(`/mailbox/export.xlsx?${query.toString()}`, { fileNameFallback: `buzon-miskipop-${new Date().toISOString().slice(0, 10)}.xlsx` });
  },
  update: (id, payload) => apiFetch(`/mailbox/messages/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  reply: (id, body) => apiFetch(`/mailbox/messages/${id}/reply`, { method: 'POST', body: JSON.stringify({ body }) }),
  markRead: (ids, read = true) => apiFetch('/mailbox/messages/read', { method: 'POST', body: JSON.stringify({ ids, read }) }),
};

export const waitingRoom = {
  getConfig: () => apiFetch('/waiting-room/config'),
  updateConfig: (config) => apiFetch('/waiting-room/config', { method: 'PUT', body: JSON.stringify(config) }),
};

export const movements = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.action) q.set('action', params.action);
    if (params.entity) q.set('entity', params.entity);
    if (params.userId) q.set('userId', params.userId);
    if (params.from) q.set('from', params.from);
    if (params.to) q.set('to', params.to);
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', params.limit);
    return apiFetch(`/movements?${q.toString()}`);
  },
};

export const testimonials = {
  list: () => apiFetch('/testimonials'),
  listPublic: () => fetch(`${API_BASE}/public/testimonials`).then((r) => r.json()),
  create: (data) => apiFetch('/testimonials', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/testimonials/${id}`, { method: 'DELETE' }),
  myData: () => apiFetch('/testimonials/my'),
  submitMine: (data) => apiFetch('/testimonials', { method: 'POST', body: JSON.stringify(data) }),
};

export const coupons = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.productId) q.set('productId', params.productId);
    if (params.active !== undefined) q.set('active', String(params.active));
    return apiFetch(`/coupons?${q.toString()}`);
  },
  get: (id) => apiFetch(`/coupons/${id}`),
  create: (data) => apiFetch('/coupons', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiFetch(`/coupons/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id) => apiFetch(`/coupons/${id}`, { method: 'DELETE' }),
};
